import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Users, ShieldCheck, BarChart3, BookOpen, Gem, Sparkles } from "lucide-react";
import { Reveal } from "../components/common/Reveal";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const ARCHES = [
  { src: "/img/ecosystem/arch-dental.webp", alt: "Periwinkle arch — Dental", h: "h-56 sm:h-72 lg:h-80 xl:h-96", accent: "periwinkle" },
  { src: "/img/ecosystem/arch-consulting.webp", alt: "Burgundy arch — Consulting", h: "h-72 sm:h-96 lg:h-[26rem] xl:h-[30rem]", accent: "burgundy" },
  { src: "/img/ecosystem/arch-creative.webp", alt: "Amber arch — Creative", h: "h-64 sm:h-80 lg:h-[22rem] xl:h-[26rem]", accent: "amber" },
];

const WORLDS = [
  {
    id: "dental",
    num: "01",
    name: "DENTAL",
    line: "Care. Smiles. Modern Dentistry.",
    desc: "Evidence-based dental care with a personal touch — from orthodontics to aesthetic and digital dentistry.",
    cta: "Explore Dental",
    href: "/dental",
    image: "/img/ecosystem/card-dental.webp",
    accentText: "group-hover:text-periwinkle",
    accentBg: "bg-periwinkle",
    accentHoverBg: "group-hover:bg-periwinkle",
  },
  {
    id: "consulting",
    num: "02",
    name: "CONSULTING",
    line: "Strategy. People. Sustainable Growth.",
    desc: "Practical strategies, training and advisory for businesses, organisations and leaders who want to grow with clarity.",
    cta: "Explore Consulting",
    href: "/consulting",
    image: "/img/ecosystem/card-consulting.webp",
    accentText: "group-hover:text-burgundy",
    accentBg: "bg-burgundy",
    accentHoverBg: "group-hover:bg-burgundy",
  },
  {
    id: "creative",
    num: "03",
    name: "CREATIVE",
    line: "Books. Writing. Ideas. Expressions.",
    desc: "Read, listen and discover creative works that inspire, reflect and connect.",
    cta: "Explore Creative",
    href: "/creative",
    image: "/img/ecosystem/card-creative.webp",
    accentText: "group-hover:text-amber",
    accentBg: "bg-amber",
    accentHoverBg: "group-hover:bg-amber",
  },
];

const VALUES = [
  { icon: Users, label: "People First" },
  { icon: ShieldCheck, label: "Evidence & Experience" },
  { icon: BarChart3, label: "Practical Strategies" },
  { icon: BookOpen, label: "Knowledge That Lasts" },
  { icon: Gem, label: "Quality In Everything" },
  { icon: Sparkles, label: "A More Inspired Tomorrow" },
];

const MARQUEE_ITEMS = ["DENTAL", "CONSULTING", "CREATIVE", "LET'S INSPIRE POSSIBILITIES"];

const Marquee = () => (
  <div className="overflow-hidden border-y border-ink/10 bg-warmwhite py-5" aria-hidden="true">
    <div className="marquee-track gap-12">
      {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
        <span key={i} className="flex items-center gap-12 font-serif text-xl italic tracking-tight text-ink/60 sm:text-2xl">
          {item} <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber" />
        </span>
      ))}
    </div>
  </div>
);

export default function UmbrellaHome() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const archY = useTransform(scrollYProgress, [0, 1], [0, 50]);

  return (
    <main data-testid="umbrella-home">
      <section ref={heroRef} className="relative overflow-hidden bg-warmwhite" data-testid="umbrella-hero">
        <div className="container-site grid items-center gap-14 py-20 sm:py-24 lg:min-h-[88vh] lg:grid-cols-12 lg:py-16">
          <div className="lg:col-span-6">
            <motion.p
              className="eyebrow"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              data-testid="umbrella-eyebrow"
            >
              iINSPIRE — One Ecosystem
            </motion.p>
            <h1 className="mt-7 font-serif text-4xl leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl xl:text-[4.4rem]" data-testid="umbrella-headline">
              <MaskedLine delay={0.35}>Care for Smiles.</MaskedLine>
              <MaskedLine delay={0.5}>Strategy for Growth.</MaskedLine>
              <MaskedLine delay={0.65} className="italic">Stories for what&apos;s Next.</MaskedLine>
            </h1>
            <motion.p
              className="mt-8 max-w-md text-base leading-relaxed text-ink/70 sm:text-lg"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.8 }}
              data-testid="umbrella-subtext"
            >
              Three connected worlds. One purpose — to help people, businesses
              and ideas grow.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.15, duration: 0.8 }}
            >
              <a
                href="#worlds"
                className="mt-10 inline-flex items-center gap-3 rounded-full bg-ink px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-warmwhite transition-colors duration-300 hover:bg-burgundy"
                data-testid="umbrella-explore-cta"
              >
                Explore iINSPIRE <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </a>
            </motion.div>
          </div>
          <motion.div
            className="flex items-end justify-center gap-3 sm:gap-5 lg:col-span-6"
            style={{ y: archY }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 1.1, ease: "easeOut" }}
            data-testid="umbrella-arches"
          >
            {ARCHES.map((a) => (
              <div key={a.src} className={`w-1/3 overflow-hidden rounded-t-full ${a.h}`}>
                <img
                  src={a.src}
                  alt={a.alt}
                  loading="eager"
                  className="h-full w-full object-cover transition-transform duration-700 hover:scale-[1.04]"
                />
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <Marquee />

      <section id="worlds" className="scroll-mt-24 bg-softgrey py-20 lg:py-28" data-testid="worlds-section">
        <div className="container-site">
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {WORLDS.map((w, i) => (
              <Reveal
                key={w.id}
                delay={i * 0.1}
                className={`h-full ${i === 2 ? "md:col-span-2 xl:col-span-1" : ""}`}
              >
                <Link
                  to={w.href}
                  data-testid={`world-card-${w.id}`}
                  className={`group flex h-full flex-col border-b-2 ${w.accentBg} bg-white transition-shadow duration-500 hover:shadow-2xl`}
                >
                  <div className="relative overflow-hidden">
                    <span className="absolute left-4 top-4 z-10 text-xs font-semibold tracking-[0.2em] text-white/90">
                      {w.num}
                    </span>
                    <img
                      src={w.image}
                      alt={`${w.name} — ${w.line}`}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-7 lg:p-8">
                    <h2 className="font-serif text-2xl tracking-wide text-ink lg:text-3xl">{w.name}</h2>
                    <p className="mt-2 text-sm font-semibold text-ink">{w.line}</p>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/65">{w.desc}</p>
                    <span className="mt-7 inline-flex items-center gap-3 text-xs font-semibold tracking-[0.05em] text-ink">
                      <span className={`flex h-10 w-10 items-center justify-center rounded-full border border-ink/20 transition-colors duration-300 group-hover:border-transparent ${w.accentHoverBg}`}>
                        <ArrowRight className="h-4 w-4 transition-colors duration-300 group-hover:text-white" strokeWidth={1.5} />
                      </span>
                      <span className={`transition-colors duration-300 ${w.accentText}`}>{w.cta}</span>
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <div className="mt-16 grid grid-cols-2 gap-y-10 border-t border-ink/10 pt-12 sm:grid-cols-3 lg:grid-cols-6" data-testid="values-strip">
              {VALUES.map((v) => (
                <div key={v.label} className="flex flex-col items-center gap-3 text-center">
                  <v.icon className="h-6 w-6 text-ink/70" strokeWidth={1.25} aria-hidden="true" />
                  <p className="text-xs font-medium leading-snug text-ink/70">{v.label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-warmwhite py-24 lg:py-36" data-testid="approach-section">
        <div className="container-site text-center">
          <Reveal>
            <p className="eyebrow">The iINSPIRE Approach</p>
            <h2 className="mx-auto mt-6 max-w-3xl font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
              One Ecosystem. <span className="italic">Real Impact.</span>
            </h2>
            <p className="mx-auto mt-8 max-w-xl text-base leading-relaxed text-ink/70 sm:text-lg">
              Different journeys, a shared purpose — to create healthier smiles,
              stronger businesses and a more inspired world.
            </p>
            <Link
              to="/about"
              className="mt-12 inline-flex items-center gap-3 rounded-full bg-ink px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-warmwhite transition-colors duration-300 hover:bg-burgundy"
              data-testid="umbrella-story-cta"
            >
              Our Story <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-warmwhite py-16" data-testid="coffee-teaser">
        <div className="container-site flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
          <Reveal>
            <p className="eyebrow">Coffee With Dr. Irma</p>
            <p className="mt-3 font-serif text-xl italic leading-relaxed text-ink/80 sm:text-2xl">
              Thoughts on health, growth, work, creativity and life.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/coffee-with-dr-irma" className="link-arrow" data-testid="coffee-teaser-link">
              Browse the editorial <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
